import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import ChapterEditorForm from '../../components/training/ChapterEditorForm';
import ReelCropSettings from '../../components/training/ReelCropSettings';
import TimestampInput from '../../components/training/TimestampInput';
import PasteClipButton from '../../components/training/PasteClipButton';
import RichTextField from '../../components/common/RichTextField';
import { useAuth } from '../../context/AuthContext';
import {
  COMPLIANCE_LABEL,
  SESSION_LEVELS,
  SUGGESTED_TRAINING_TAGS,
} from '../../constants/training';
import {
  adminLinkSessionToPath,
  checkIsAdminOrHigher,
  createSessionReels,
  fetchDistinctTags,
  fetchSessionById,
  fetchTrainingPaths,
  publishSessionDraft,
  saveSessionDraft,
  suggestSessionPathLink,
} from '../../services/training';
import { DEFAULT_CROP_SETTINGS } from '../../utils/reelCrop';
import { chaptersFromForm, chaptersFromFormLenient, formatTimestamp, parseTimestampInput } from '../../utils/videoTime';
import { isRichContentEmpty } from '../../utils/richText';

const EMPTY_CLIP = { start: '', end: '', caption: '', ...DEFAULT_CROP_SETTINGS };
const EMPTY_CHAPTER = { timestamp: '', title: '' };
const PLACEHOLDER_TITLE = 'Borrador sin título';

function FieldLabel({ children, required }) {
  return (
    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
      {children}
      {required && <span className="text-brand-cyan ml-1">*</span>}
    </label>
  );
}

function TextInput({ className = '', ...props }) {
  return (
    <input
      className={`w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] placeholder-[var(--text-secondary)]/50 focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-all ${className}`}
      {...props}
    />
  );
}

export default function SessionDraftPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageTraining, loading: authLoading } = useAuth();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tagOptions, setTagOptions] = useState(SUGGESTED_TRAINING_TAGS);
  const [trainingPaths, setTrainingPaths] = useState([]);
  const [suggestedPathId, setSuggestedPathId] = useState('');

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [transcript, setTranscript] = useState('');
  const [actionItems, setActionItems] = useState(['']);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [level, setLevel] = useState('both');
  const [compliance, setCompliance] = useState(false);
  const [createReels, setCreateReels] = useState(false);
  const [reelClips, setReelClips] = useState([{ ...EMPTY_CLIP }]);
  const [chapterRows, setChapterRows] = useState([{ ...EMPTY_CHAPTER }]);

  const [submitting, setSubmitting] = useState(false);
  const [savePhase, setSavePhase] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  useEffect(() => {
    fetchDistinctTags().then(setTagOptions).catch(console.error);
    fetchTrainingPaths().then(setTrainingPaths).catch(console.error);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchSessionById(id)
      .then((row) => {
        if (!active) return;
        if (!row) {
          setError('Borrador no encontrado');
          setSession(null);
          return;
        }
        if (!row.isDraft) {
          navigate(`/app/entrenamientos/${row.id}`, { replace: true });
          return;
        }
        setSession(row);
        setTitle(row.title === PLACEHOLDER_TITLE ? '' : (row.title || ''));
        setSummary(row.summary || '');
        setTranscript(row.transcript || '');
        setActionItems(row.actionPoints?.length ? row.actionPoints : ['']);
        setTags(row.tags || []);
        setLevel(row.levelValue || 'both');
        setCompliance(Boolean(row.complianceConfirmed));
        if (row.chapters?.length) {
          setChapterRows(
            row.chapters.map((ch) => ({
              timestamp: formatTimestamp(ch.startSeconds),
              title: ch.title || '',
            }))
          );
        }
      })
      .catch((err) => {
        if (active) setError(err.message || 'No se pudo cargar el borrador');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [id, navigate]);

  const allTagSuggestions = useMemo(() => {
    const set = new Set([...SUGGESTED_TRAINING_TAGS, ...tagOptions]);
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [tagOptions]);

  const videoDuration = session?.durationSeconds ?? null;
  const videoUrl = session?.videoUrl;

  const addTag = (value) => {
    const trimmed = value.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    setTags([...tags, trimmed]);
  };

  const removeTag = (tag) => setTags(tags.filter((t) => t !== tag));

  const updateActionItem = (index, value) => {
    setActionItems((prev) => prev.map((item, i) => (i === index ? value : item)));
  };

  const addActionItem = () => setActionItems((prev) => [...prev, '']);
  const removeActionItem = (index) => {
    setActionItems((prev) => (prev.length <= 1 ? [''] : prev.filter((_, i) => i !== index)));
  };

  const updateReelClip = (index, field, value) => {
    setReelClips((prev) => prev.map((clip, i) => (i === index ? { ...clip, [field]: value } : clip)));
  };

  const updateReelClipCrop = (index, cropValue) => {
    setReelClips((prev) => prev.map((clip, i) => (i === index ? { ...clip, ...cropValue } : clip)));
  };

  const addReelClip = () => setReelClips((prev) => [...prev, { ...EMPTY_CLIP }]);
  const removeReelClip = (index) => {
    setReelClips((prev) => (prev.length <= 1 ? [{ ...EMPTY_CLIP }] : prev.filter((_, i) => i !== index)));
  };

  const updateChapterRow = (index, field, value) => {
    setChapterRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addChapterRow = () => setChapterRows((prev) => [...prev, { ...EMPTY_CHAPTER }]);
  const removeChapterRow = (index) => {
    setChapterRows((prev) => (prev.length <= 1 ? [{ ...EMPTY_CHAPTER }] : prev.filter((_, i) => i !== index)));
  };

  const buildDraftPayload = () => {
    const chapters = chaptersFromFormLenient(chapterRows).filter(
      (ch) => !videoDuration || ch.startSeconds <= videoDuration
    );
    return {
      title: title.trim() || PLACEHOLDER_TITLE,
      summary: summary ?? '',
      transcript: transcript ?? '',
      actionItems: actionItems.map((a) => a.trim()).filter(Boolean),
      tags,
      level,
      chapters,
      complianceConfirmed: compliance,
    };
  };

  const buildPublishPayload = () => {
    let chapters = [];
    try {
      chapters = chaptersFromForm(chapterRows);
    } catch (err) {
      throw new Error(err.message);
    }
    if (videoDuration && chapters.some((ch) => ch.startSeconds > videoDuration)) {
      throw new Error(`Un capítulo excede la duración del video (${formatTimestamp(videoDuration)})`);
    }
    return {
      title: title.trim() || PLACEHOLDER_TITLE,
      summary: summary.trim(),
      transcript: transcript.trim(),
      actionItems: actionItems.map((a) => a.trim()).filter(Boolean),
      tags,
      level,
      chapters,
      complianceConfirmed: compliance,
    };
  };

  const linkCaminoIfNeeded = async (sessionId) => {
    if (!suggestedPathId) return;
    try {
      const isAdmin = await checkIsAdminOrHigher();
      if (isAdmin) {
        await adminLinkSessionToPath(suggestedPathId, sessionId);
      } else {
        await suggestSessionPathLink(sessionId, suggestedPathId);
      }
    } catch {
      // Silent — Camino suggestion must not block save/publish
    }
  };

  const createClipsIfNeeded = async () => {
    if (!createReels || !session) return;
    const clips = [];
    for (const clip of reelClips) {
      if (!clip.start && !clip.end && !clip.caption.trim()) continue;
      const start = parseTimestampInput(clip.start, 'Inicio del clip');
      const end = parseTimestampInput(clip.end, 'Fin del clip');
      if (end <= start) throw new Error('El fin del clip debe ser posterior al inicio');
      if (videoDuration && end > videoDuration) {
        throw new Error(`El clip no puede terminar después de ${formatTimestamp(videoDuration)}`);
      }
      clips.push({
        clipStartSeconds: start,
        clipEndSeconds: end,
        caption: clip.caption.trim() || title.trim() || 'Clip',
        cropMode: clip.cropMode,
        focalX: clip.focalX,
        focalY: clip.focalY,
      });
    }
    if (clips.length > 0) {
      setSavePhase('Creando reels…');
      await createSessionReels({
        sessionId: session.id,
        publitioVideoId: session.publitioVideoId,
        videoUrl: session.videoUrl,
        thumbnailUrl: session.thumbnailUrl,
        clips,
      });
    }
  };

  const handleSaveDraft = async () => {
    setError('');
    setInfo('');
    setSubmitting(true);
    setSavePhase('Guardando borrador…');
    try {
      const payload = buildDraftPayload();
      await saveSessionDraft(session.id, payload);
      setInfo('Borrador guardado. Puedes volver más tarde sin volver a subir el video.');
    } catch (err) {
      setError(err.message || 'No se pudo guardar el borrador');
    } finally {
      setSubmitting(false);
      setSavePhase('');
    }
  };

  const handlePublish = async () => {
    setError('');
    setInfo('');
    if (!title.trim()) {
      setError('El título es obligatorio');
      return;
    }
    if (isRichContentEmpty(summary)) {
      setError('El resumen es obligatorio');
      return;
    }
    if (!compliance) {
      setError('Debes confirmar el cumplimiento de políticas');
      return;
    }

    setSubmitting(true);
    setSavePhase('Publicando sesión…');
    try {
      const payload = buildPublishPayload();
      await publishSessionDraft(session.id, {
        ...payload,
        title: title.trim(),
        complianceConfirmed: true,
      });
      await linkCaminoIfNeeded(session.id);
      await createClipsIfNeeded();
      navigate(`/app/entrenamientos/${session.id}`, { replace: true, state: { uploadSuccess: true } });
    } catch (err) {
      setError(err.message || 'No se pudo publicar la sesión');
    } finally {
      setSubmitting(false);
      setSavePhase('');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-[var(--text-secondary)] text-sm font-bold uppercase tracking-widest">
        Cargando…
      </div>
    );
  }

  if (!canManageTraining) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  if (!session) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <p className="text-red-300 text-sm">{error || 'Borrador no encontrado'}</p>
        <Link to="/app/entrenamientos/subir" className="text-brand-cyan text-sm font-bold mt-4 inline-block">
          Volver a subir
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28">
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/app/entrenamientos/subir"
          className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors"
        >
          <SafeIcon name="ArrowLeft" className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[var(--text-main)] uppercase tracking-tight italic">
            Detalles de la sesión
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Paso 2 de 2 — completa los datos. Guarda como borrador o publica cuando esté listo.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-red-400/40 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}
      {info && (
        <div className="mb-6 p-4 rounded-xl border border-brand-green/40 bg-brand-green/10 text-[var(--text-main)] text-sm">
          {info}
        </div>
      )}

      {videoUrl && (
        <section className="mb-8 p-4 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)]">
          <FieldLabel>Video subido</FieldLabel>
          <video
            src={videoUrl}
            controls
            className="w-full rounded-xl border border-[var(--border-subtle)] max-h-72 bg-black"
          />
          {videoDuration != null && (
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Duración: {formatTimestamp(videoDuration)}
            </p>
          )}
        </section>
      )}

      <div className="space-y-8">
        <section className="space-y-4">
          <div>
            <FieldLabel required>Título</FieldLabel>
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} disabled={submitting} />
          </div>
          <div>
            <FieldLabel required>Resumen</FieldLabel>
            <RichTextField value={summary} onChange={setSummary} disabled={submitting} minHeight="140px" />
          </div>
          <div>
            <FieldLabel>Transcripción</FieldLabel>
            <RichTextField
              value={transcript}
              onChange={setTranscript}
              disabled={submitting}
              placeholder="Opcional — pega la transcripción si la tienes"
              minHeight="200px"
            />
          </div>
        </section>

        <section className="space-y-3">
          <FieldLabel>Puntos de acción</FieldLabel>
          {actionItems.map((item, index) => (
            <div key={index} className="flex gap-2">
              <TextInput
                value={item}
                onChange={(e) => updateActionItem(index, e.target.value)}
                disabled={submitting}
                placeholder={`Punto ${index + 1}`}
              />
              <button
                type="button"
                onClick={() => removeActionItem(index)}
                disabled={submitting}
                className="px-3 text-[var(--text-secondary)] hover:text-red-400"
                aria-label="Quitar punto"
              >
                <SafeIcon name="X" className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addActionItem}
            disabled={submitting}
            className="text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green"
          >
            + Añadir punto
          </button>
        </section>

        <section className="space-y-3">
          <FieldLabel>Etiquetas</FieldLabel>
          <div className="flex flex-wrap gap-2 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full gfi-gradient-bg text-brand-navy text-[10px] font-black uppercase tracking-wider"
              >
                {tag}
                <button type="button" onClick={() => removeTag(tag)} disabled={submitting}>
                  <SafeIcon name="X" className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <TextInput
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              disabled={submitting}
              placeholder="Escribe y pulsa Enter"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag(tagInput);
                  setTagInput('');
                }
              }}
            />
            <button
              type="button"
              disabled={submitting}
              onClick={() => { addTag(tagInput); setTagInput(''); }}
              className="px-4 rounded-xl border border-[var(--border-subtle)] text-[10px] font-black uppercase tracking-widest"
            >
              Añadir
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {allTagSuggestions.filter((t) => !tags.includes(t)).map((tag) => (
              <button
                key={tag}
                type="button"
                disabled={submitting}
                onClick={() => addTag(tag)}
                className="px-3 py-1 rounded-full border border-[var(--border-subtle)] text-[10px] font-bold text-[var(--text-secondary)] hover:border-brand-cyan hover:text-[var(--text-main)]"
              >
                {tag}
              </button>
            ))}
          </div>
        </section>

        <section>
          <FieldLabel required>Nivel</FieldLabel>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            disabled={submitting}
            className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green"
          >
            {SESSION_LEVELS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </section>

        {trainingPaths.length > 0 && (
          <section>
            <FieldLabel>Camino (opcional)</FieldLabel>
            <select
              value={suggestedPathId}
              onChange={(e) => setSuggestedPathId(e.target.value)}
              disabled={submitting}
              className="w-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl py-3 px-4 text-sm text-[var(--text-main)] focus:outline-none focus:border-brand-green"
            >
              <option value="">Sin Camino</option>
              {trainingPaths.map((path) => (
                <option key={path.id} value={path.id}>{path.title}</option>
              ))}
            </select>
          </section>
        )}

        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <FieldLabel>Capítulos del video (opcional)</FieldLabel>
          <p className="text-xs text-[var(--text-secondary)] mb-4">
            Añade marcas de tiempo para que los miembros salten a secciones del video.
          </p>
          <ChapterEditorForm
            rows={chapterRows}
            onChange={updateChapterRow}
            onAdd={addChapterRow}
            onRemove={removeChapterRow}
            disabled={submitting}
            maxDurationSeconds={videoDuration}
          />
        </section>

        <section className="p-6 rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-surface)] space-y-4">
          <div className="flex items-start gap-3">
            <input
              id="create-reels"
              type="checkbox"
              checked={createReels}
              onChange={(e) => setCreateReels(e.target.checked)}
              disabled={submitting}
              className="mt-1 h-4 w-4 rounded border-[var(--border-subtle)]"
            />
            <label htmlFor="create-reels" className="text-sm text-[var(--text-main)] font-medium">
              También publicar uno o más clips como reels de esta sesión
            </label>
          </div>

          {createReels && (
            <div className="space-y-6 pt-2 border-t border-[var(--border-subtle)]">
              {reelClips.map((clip, index) => (
                <div key={index} className="space-y-3 p-4 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)]">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                      Clip {index + 1}
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <PasteClipButton
                        disabled={submitting}
                        onParsed={({ title, start, end }) => {
                          setReelClips((prev) =>
                            prev.map((c, i) =>
                              i === index
                                ? { ...c, caption: title, start, end }
                                : c
                            )
                          );
                        }}
                      />
                      {reelClips.length > 1 && (
                        <button type="button" onClick={() => removeReelClip(index)} disabled={submitting} className="text-red-400 text-[10px] font-bold uppercase">
                          Quitar
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <FieldLabel>Inicio</FieldLabel>
                      <TimestampInput
                        value={clip.start}
                        onChange={(e) => updateReelClip(index, 'start', e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div>
                      <FieldLabel>Fin</FieldLabel>
                      <TimestampInput
                        value={clip.end}
                        onChange={(e) => updateReelClip(index, 'end', e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Caption del reel</FieldLabel>
                    <TextInput
                      value={clip.caption}
                      onChange={(e) => updateReelClip(index, 'caption', e.target.value)}
                      disabled={submitting}
                      placeholder="Opcional — usa el título de la sesión si está vacío"
                    />
                  </div>
                  {videoUrl && (
                    <ReelCropSettings
                      videoSrc={videoUrl}
                      aspectRatio={null}
                      value={{
                        cropMode: clip.cropMode,
                        focalX: clip.focalX,
                        focalY: clip.focalY,
                      }}
                      onChange={(crop) => updateReelClipCrop(index, crop)}
                      disabled={submitting}
                      compact
                    />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addReelClip}
                disabled={submitting}
                className="text-[10px] font-black uppercase tracking-widest text-brand-cyan hover:text-brand-green"
              >
                + Otro clip
              </button>
            </div>
          )}
        </section>

        <section className="p-4 rounded-xl border border-brand-cyan/30 bg-brand-cyan/5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={compliance}
              onChange={(e) => setCompliance(e.target.checked)}
              disabled={submitting}
              className="mt-1 h-4 w-4 rounded border-[var(--border-subtle)]"
            />
            <span className="text-sm text-[var(--text-main)] leading-relaxed">{COMPLIANCE_LABEL}</span>
          </label>
        </section>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={submitting}
            className="flex-1 flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-surface)] font-black text-xs uppercase tracking-widest text-[var(--text-main)] hover:border-brand-cyan disabled:opacity-50"
          >
            {submitting && savePhase.includes('borrador') ? (
              <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
            ) : (
              <SafeIcon name="FileText" className="w-5 h-5" />
            )}
            Guardar como borrador
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={submitting || !compliance}
            className="flex-1 flex items-center justify-center gap-2 gfi-gradient-bg text-brand-navy px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 shadow-xl"
          >
            {submitting && !savePhase.includes('borrador') ? (
              <>
                <SafeIcon name="Loader" className="w-5 h-5 animate-spin" />
                {savePhase || 'Publicando…'}
              </>
            ) : (
              <>
                <SafeIcon name="Upload" className="w-5 h-5" />
                Publicar sesión
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
